import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm';

/**
 * Prullenbak: soft-verwijderde charts met een herstelknop per rij. Verwijderen
 * was al soft-delete; dit maakt het eindelijk omkeerbaar vanuit de UI.
 */
@Component({
  selector: 'hd-chart-trash-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-chart-trash-dialog.component.html',
  styleUrl: './hd-chart-trash-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartTrashDialogComponent {
  private readonly groupService = inject(HdChartGroupService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  /** Emit na elke geslaagde restore zodat de manager zijn lijst kan verversen. */
  readonly restored = output<void>();

  protected readonly items = signal<PlatformChartListItemDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal('');
  /** Loopt "Alles definitief verwijderen": knoppen dicht tot de reeks klaar is. */
  protected readonly purgingAll = signal(false);

  constructor() {
    effect(() => {
      if (this.open()) void this.load();
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chartManager.trash.title'),
      empty: translate('humanDesign.chartManager.trash.empty'),
      restore: translate('humanDesign.chartManager.trash.restore'),
      purge: translate('humanDesign.chartManager.trash.purge'),
      purgeAll: translate('humanDesign.chartManager.trash.purgeAll'),
      loading: translate('humanDesign.chartManager.detail.loading'),
    };
  });

  protected onOpenChange(open: boolean): void {
    this.open.set(open);
  }

  protected formatDeletedOn(timestamp: string | null | undefined): string {
    if (!timestamp) return '—';
    const parsed = moment(timestamp);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : '—';
  }

  protected async restore(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    this.error.set('');
    try {
      await this.groupService.restoreChart(item.id);
      this.items.update(current => current.filter(candidate => candidate.id !== item.id));
      this.restored.emit();
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /** Permanent verwijderen: expliciete bevestiging; herstellen kan daarna niet meer. */
  protected async purge(item: PlatformChartListItemDto): Promise<void> {
    if (!item.id) return;
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.trash.purgeConfirm',
      { name: item.name },
      this.contentLang.effectiveLang(),
    );
    const ok = await this.confirmer.confirm(message, { color: 'danger' });
    if (!ok) return;

    this.error.set('');
    try {
      await this.groupService.purgeChart(item.id);
      this.items.update(current => current.filter(candidate => candidate.id !== item.id));
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  /**
   * Alles in de prullenbak definitief verwijderen, na één bevestiging. Chart per
   * chart via dezelfde purge-call; bij een fout stopt de reeks en blijven de
   * resterende charts in de lijst staan.
   */
  protected async purgeAll(): Promise<void> {
    const targets = this.items().filter(item => !!item.id);
    if (targets.length === 0 || this.purgingAll()) return;
    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.trash.purgeAllConfirm',
      { count: targets.length },
      this.contentLang.effectiveLang(),
    );
    const ok = await this.confirmer.confirm(message, { color: 'danger' });
    if (!ok) return;

    this.error.set('');
    this.purgingAll.set(true);
    try {
      for (const item of targets) {
        await this.groupService.purgeChart(item.id!);
        this.items.update(current => current.filter(candidate => candidate.id !== item.id));
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.purgingAll.set(false);
    }
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      this.items.set(await this.groupService.listDeletedCharts());
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }
}
